import { ChevronRight } from 'lucide-react';
import { ColumnDef } from '@tanstack/react-table';
import { capitalize } from '@app/utils';
import { PmProductsParams, ProductEntity, ProductStatus, UserEntity, useListPmProducts } from '@data';
import React, { useMemo, ReactElement, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganizationId } from '@app/utils/hooks';
import { MultiSelectField } from '@ui/components/ui/multi-select';
import { DataTable, DataTableColumnHeader } from '@ui/components/ui/data-table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/ui/tooltip';
import { ClearFilterButton, FilterField } from '@app/components/core';
import { Roles } from '@app/utils/types';
import { useOrganizations } from '@data/hooks/organizations';
import { useViewTransitionNav } from '@app/components/core/viewTransition';
import { productPath } from '@app/constants/url';
import { filterBarClass, pageHeaderRowClass, pageTitleClass } from '@app/constants/styles';
import { Loader } from '@ui';

import { clientLabelOf, clientPickerOptions, narrowAndOrderProducts, productPickerOptions } from './productRows';
import AddProductDialog from './AddProductDialog';

type ProductsListRow = ProductEntity & { name: string };

const allStatuses = Object.values(ProductStatus) as ProductStatus[];

function ProductsListLoaded({ organizationId, user }: { organizationId: string; user: UserEntity }): ReactElement {
  const [statuses, setStatuses] = useState<ProductStatus[]>([]);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [clientNames, setClientNames] = useState<string[]>([]);

  const productPmParams: PmProductsParams = { statuses: statuses.length ? statuses : allStatuses };

  const { data: listPmProducts, isLoading: listPmProductsIsLoading } = useListPmProducts(
    organizationId,
    productPmParams,
  );

  // Only an admin may create a product, which is what the route enforces, so the button is theirs alone.
  const { data: organizations } = useOrganizations(user.id);
  const canAddProduct = organizations?.items?.find(o => o.id === organizationId)?.role === Roles.admin;

  const { t } = useTranslation();
  const transitionNavigate = useViewTransitionNav();
  const openProduct = (product: ProductEntity): void =>
    transitionNavigate(productPath(organizationId, product.id), 'forward');

  const renderManagers = (managers?: string[]) => {
    if (!managers?.length) {
      return <span className="text-muted-foreground">-</span>;
    }
    const [firstManager] = managers;
    const remaining = managers.length - 1;
    if (remaining <= 0) {
      return <span>{firstManager}</span>;
    }
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-default">
            {firstManager} <span className="font-bold">{t('nMore', { count: remaining })}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent>{managers.join(', ')}</TooltipContent>
      </Tooltip>
    );
  };

  const statusOptions = allStatuses
    .slice()
    .sort((a, b) => a.localeCompare(b))
    .map(x => ({ value: x, label: capitalize(x) ?? x }));

  // The picker lists the products the status filter already allows; it only narrows what is on screen.
  // Filtering server-side instead would shrink the options to the products already picked.
  const knownNames = useRef(new Map<string, string>());
  const productOptions = useMemo(
    () =>
      productPickerOptions(
        listPmProducts?.items ?? [],
        selectedProductIds,
        knownNames.current,
        p => capitalize(p.name) ?? p.name ?? '',
      ),
    [listPmProducts, selectedProductIds],
  );

  const visibleProducts = useMemo(
    () => narrowAndOrderProducts(listPmProducts?.items ?? [], selectedProductIds, clientNames),
    [listPmProducts, selectedProductIds, clientNames],
  );

  // Every client of the organization, not only the ones already linked: the filter is for finding a
  // client's products, and a client with none is a fair thing to ask about.
  // Built from the products' own clients rather than from the client list, so the options and the column
  // can only ever say the same thing. Off the unnarrowed list, so choosing one does not remove the others.
  const clientOptions = useMemo(
    () => clientPickerOptions(listPmProducts?.items ?? [], clientNames, t('noClient')),
    [listPmProducts, clientNames, t],
  );

  /* eslint-disable react/no-unstable-nested-components -- TanStack Table column render props */
  const columns = useMemo<ColumnDef<ProductsListRow>[]>(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }) => <DataTableColumnHeader column={column} title={t('titles.products')} />,
        cell: ({ row }) => <div className="min-w-[8rem] font-medium">{row.original.name}</div>,
      },
      {
        id: 'client',
        accessorFn: row => clientLabelOf(row),
        header: ({ column }) => <DataTableColumnHeader column={column} title={t('client')} />,
        cell: ({ row }) => clientLabelOf(row.original) || <span className="text-muted-foreground">-</span>,
      },
      {
        id: 'managers',
        accessorFn: row => row.managers?.join(', ') ?? '',
        header: ({ column }) => <DataTableColumnHeader column={column} title={t('managers')} />,
        cell: ({ row }) => <div className="min-w-[10rem]">{renderManagers(row.original.managers)}</div>,
      },
      {
        id: 'repositories',
        accessorFn: row => row.repositories.join(', '),
        header: ({ column }) => <DataTableColumnHeader column={column} title={t('repositories')} />,
        // Read-only here now: the product's Connected apps card is where a repository is linked, and two
        // places to change one link is what this epic exists to fix.
        cell: ({ row }) => (
          <div className="min-w-[10rem]">
            {row.original.repositories.length ? (
              row.original.repositories.join(', ')
            ) : (
              <span className="text-muted-foreground">-</span>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: ({ column }) => <DataTableColumnHeader column={column} title={t('status')} />,
        cell: ({ row }) => capitalize(row.original.status),
      },
      {
        id: 'chevron',
        enableSorting: false,
        header: '',
        meta: { className: 'w-0 text-right' },
        cell: () => <ChevronRight className="size-4 text-muted-foreground" />,
      },
    ],
    [t],
  );
  /* eslint-enable react/no-unstable-nested-components */

  const rows = visibleProducts.map(x => ({ ...x, name: capitalize(x.name) ?? x.name }));

  // Every status on screen is the default, so the Clear button stays hidden until something is actually
  // narrowed. Clearing returns to that default.
  const filtersActive = selectedProductIds.length > 0 || statuses.length > 0 || clientNames.length > 0;
  const clearFilters = (): void => {
    setSelectedProductIds([]);
    setStatuses([]);
    setClientNames([]);
  };

  return (
    <>
      <div className={`${pageHeaderRowClass} mb-5 flex flex-col gap-4 md:flex-row md:items-center md:justify-between`}>
        <h1 className={pageTitleClass}>{t('titles.products')}</h1>
        {canAddProduct && <AddProductDialog organizationId={organizationId} />}
      </div>

      <div className={`mb-6 ${filterBarClass}`}>
        <FilterField>
          <MultiSelectField
            label={t('client')}
            options={clientOptions}
            value={clientNames}
            onChange={setClientNames}
            emptyLabel={t('allClients')}
            searchPlaceholder={t('search')}
            emptySearchLabel={t('noMatches')}
            renderCountSummary={count => t('nItemsSelected', { count })}
          />
        </FilterField>

        <FilterField>
          <MultiSelectField
            label={t('titles.products')}
            options={productOptions}
            value={selectedProductIds}
            onChange={setSelectedProductIds}
            emptyLabel={t('allProducts')}
            searchPlaceholder={t('search')}
            emptySearchLabel={t('noMatches')}
            renderCountSummary={count => t('nItemsSelected', { count })}
          />
        </FilterField>

        <FilterField>
          <MultiSelectField
            label={t('status')}
            options={statusOptions}
            value={statuses}
            onChange={vals => setStatuses(vals as ProductStatus[])}
            emptyLabel={t('allStatuses')}
            searchable={false}
            renderCountSummary={count => t('nItemsSelected', { count })}
          />
        </FilterField>

        {filtersActive && <ClearFilterButton onClick={clearFilters} />}
      </div>

      <div>
        {listPmProducts && (
          <>
            <div className="hidden md:block">
              <DataTable<ProductsListRow>
                columns={columns}
                data={rows}
                isLoading={listPmProductsIsLoading}
                initialSorting={[]}
                getRowId={row => row.id}
                onRowClick={openProduct}
                padded
              />
            </div>
            <div className="md:hidden">
              {/* One list across the screen, a row per product, rather than a stack of inset cards. */}
              <div className="-mx-4 flex flex-col divide-y divide-border border-y border-border">
                {visibleProducts.map(product => (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => openProduct(product)}
                    className="flex items-center gap-3 bg-card p-4 text-left text-card-foreground transition-colors hover:bg-accent"
                  >
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <div className="flex items-center justify-between gap-3">
                        <h3 className="truncate text-lg font-bold text-foreground">{capitalize(product.name)}</h3>
                        <span className="shrink-0 text-sm font-semibold text-foreground">
                          {capitalize(product.status)}
                        </span>
                      </div>
                      <p className="text-sm text-foreground">
                        {t('client')}: {clientLabelOf(product) || '-'}
                      </p>
                      <p className="text-sm text-foreground">
                        {t('managers')}: {renderManagers(product.managers)}
                      </p>
                    </div>
                    <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

const ProductsList = ({ user }: { user: UserEntity }): ReactElement => {
  const organizationId = useOrganizationId();
  if (!organizationId) {
    return <Loader />;
  }
  return <ProductsListLoaded organizationId={organizationId} user={user} />;
};

export default ProductsList;
